import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Alert, Box, Button, Stack, Typography, IconButton, Skeleton, screenSizeSmaller} from 'views/components';
import {CheckCircleOutline, Close} from 'views/icons';
import {Translate} from 'views/module';

import CaregiverFeedbackDrawer from './CaregiverFeedbackDrawer';

const CaregiverFeedbackSection = (props) => {
  const {
    clientRn,
    caregiverRn,
    questions,
    createCaregiverFeedback,
    getCaregiverFeedback,
    statusProgressWrap: progressWrap,
    userRn,
  } = props;

  const {
    firstName,
  } = questions;

  const smallScreen = screenSizeSmaller('navbarHide');

  const [isLoading, setIsLoading] = React.useState(true);
  const [isFeedbackSending, setIsFeedbackSending] = React.useState(false);
  const [isFeedbackDrawerOpen, setIsFeedbackDrawerOpen] = React.useState(false);
  const [isMatch, setIsMatch] = React.useState('NEITHER');
  const [selectedOptions, setSelectedOptions] = React.useState([]);
  const [additionalComments, setAdditionalComments] = React.useState('');
  const [isSubmissionAlertOpen, setIsSubmissionAlertOpen] = React.useState(false);

  React.useEffect(() => {
    progressWrap(async () => {
      try {
        const result = await getCaregiverFeedback(clientRn, caregiverRn);

        const feedback = result.feedback;
        if (feedback) {
          const {
            isMatch,
            selectedOptions,
            additionalComments,
          } = feedback;
          setIsMatch(isMatch);
          setSelectedOptions(selectedOptions);
          setAdditionalComments(additionalComments);
        }
      } finally {
        setIsLoading(false);
      }
    });
  }, []);

  React.useEffect(() => {
    if (!isSubmissionAlertOpen) return;
    const timer = setTimeout(() => setIsSubmissionAlertOpen(false), 10000);
    return () => clearTimeout(timer);
  }, [isSubmissionAlertOpen]);

  const handleSubmitFeedback = () => {
    progressWrap(async () => {
      setIsFeedbackSending(true);
      try {
        await createCaregiverFeedback(
            clientRn,
            caregiverRn,
            {
              clientRelationshipRn: userRn,
              feedback: {
                isMatch: isMatch,
                selectedOptions: selectedOptions,
                additionalComments: additionalComments,
              },
            },
        );
        setIsFeedbackDrawerOpen(false);
        setIsSubmissionAlertOpen(true);
      } finally {
        setIsFeedbackSending(false);
      }
    });
  };

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          left: smallScreen ? 0 : 312,
          right: 0,
          bottom: 0,
          zIndex: 1200,
          bgcolor: '#FFFDFC',
          borderTop: '6px solid #DA7955',
          px: {xs: 2, sm: 5},
          py: {xs: 2, sm: 3},
        }}
      >
        {
          isLoading && (
            <Stack
              direction={{xs: 'column', sm: 'row'}}
              justifyContent="space-between"
              alignItems="center"
              spacing={{xs: 1, sm: 2}}
              sx={{
                width: '100%',
                maxWidth: {md: 700, lg: 1100},
                mx: 'auto',
              }}
            >
              <Stack spacing={1} sx={{width: {xs: '100%', sm: 'auto'}}}>
                <Skeleton variant="text" width={220} height={28}/>
                <Skeleton variant="text" width={280} height={20}/>
              </Stack>
              <Stack
                direction='row'
                spacing={2}
                sx={{width: {xs: '100%', sm: 'auto'}}}
              >
                <Skeleton
                  variant="rectangular"
                  height={56}
                  sx={{
                    width: {xs: '100%', sm: 180},
                    borderRadius: 3,
                  }}
                />
                <Skeleton
                  variant="rectangular"
                  height={56}
                  sx={{
                    width: {xs: '100%', sm: 180},
                    borderRadius: 3,
                  }}
                />
              </Stack>
            </Stack>
          )
        }
        {
          !isLoading && (
            <Stack
              direction={{xs: 'column', sm: 'row'}}
              justifyContent="space-between"
              alignItems="center"
              spacing={{xs: 3, sm: 2}}
              sx={{
                width: '100%',
                maxWidth: {md: 700, lg: 1100},
                mx: 'auto',
              }}
            >
              {/* Titles */}
              <Stack width="100%" spacing={{xs: 1, md: 1}}>
                <Typography
                  variant="h6"
                  sx={{
                    color: '#000000',
                    fontWeight: 600,
                  }}
                >
                  <Translate
                    expression='caregiverDetail.CaregiverFeedback.PanelTitle'
                    substitute={{caregiverName: firstName}}
                  />
                </Typography>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: '#2D2925',
                    fontWeight: 500,
                  }}
                >
                  <Translate
                    expression='caregiverDetail.CaregiverFeedback.PanelSubtitle'
                  />
                </Typography>
              </Stack>
              {/* Match Buttons */}
              <Stack
                direction="row"
                spacing={2}
                width="100%"
              >
                {/* Not A Match Button */}
                <Button
                  variant={!isMatch ? 'contained' : 'outlined'}
                  disableElevation
                  sx={{
                    'width': '100%',
                    'py': 1.75,
                    'px': {xs: 1, md: 2},
                    'borderRadius': '9px',
                    'borderStyle': 'solid',
                    'color': !isMatch ? 'color.white' : 'color.black',
                    'bgcolor': !isMatch ? 'error.main' : 'color.white',
                    '&:hover': {
                      bgcolor: !isMatch ? 'error.dark' : '#fff5f5',
                      color: !isMatch ? 'color.white' : 'error.main',
                      borderColor: !isMatch ? 'grey.400' : 'error.main',
                      ...(isMatch && {
                        transform: 'scale(1.07)',
                        transition: 'transform 0.15s',
                      }),
                    },
                    'borderColor': !isMatch ? 'error.main' : 'grey.400',
                    'borderWidth': 1,
                  }}
                  onClick={() => {
                    setIsMatch(false);
                    setIsFeedbackDrawerOpen(true);
                  }}
                  disabled={isFeedbackSending}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 500,
                      textTransform: 'none',
                    }}
                  >
                    <Translate expression="caregiverDetail.CaregiverFeedback.IsNotAMatchButton" />
                  </Typography>
                </Button>
                {/* Is A Match Button */}
                <Button
                  variant={(isMatch === true || isMatch === 'NEITHER') ? 'contained' : 'outlined'}
                  disableElevation
                  color="success"
                  sx={{
                    'width': '100%',
                    'py': 1.75,
                    'px': {xs: 1, md: 2},
                    'borderRadius': '9px',
                    'borderStyle': 'solid',
                    'borderColor': (isMatch === true) ? '#68A873' : (isMatch === 'NEITHER') ? 'primary.main' : 'grey.400',
                    'borderWidth': 1.,
                    'color': (isMatch === true) ? 'color.white' : (isMatch === 'NEITHER') ? 'color.white' : 'color.black',
                    'bgcolor': (isMatch === true) ? '#68A873' : (isMatch === 'NEITHER') ? 'primary.main' : 'color.white',
                    '&:hover': {
                      borderColor: (isMatch === true) ? 'grey.400' : (isMatch === 'NEITHER') ? 'grey.400' : '#68A873',
                      bgcolor: (isMatch === true) ? '#5E9B6B' : (isMatch === 'NEITHER') ? 'primary.dark' : '#F4FBF7',
                      color: (isMatch === true) ? 'color.white' : (isMatch === 'NEITHER') ? 'color.white' : '#68A873',
                      ...((isMatch !== true) && {
                        transform: 'scale(1.07)',
                        transition: 'transform 0.15s',
                      }),
                    },
                  }}
                  onClick={() => {
                    setIsMatch(true);
                    setIsFeedbackDrawerOpen(true);
                  }}
                  disabled={isFeedbackSending}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 500,
                      textTransform: 'none',
                    }}
                  >
                    <Translate
                      expression="caregiverDetail.CaregiverFeedback.IsAMatchButton"
                      substitute={{caregiverName: firstName}}
                    />
                  </Typography>
                </Button>
              </Stack>
            </Stack>
          )
        }
      </Box>
      {
        isMatch !== 'NEITHER' &&
        <CaregiverFeedbackDrawer
          isMatch={isMatch}
          caregiverFirstName={firstName}
          selectedOptions={selectedOptions}
          setSelectedOptions={setSelectedOptions}
          additionalComments={additionalComments}
          setAdditionalComments={setAdditionalComments}
          isFeedbackDrawerOpen={isFeedbackDrawerOpen}
          setIsFeedbackDrawerOpen={setIsFeedbackDrawerOpen}
          handleSubmitFeedback={handleSubmitFeedback}
          isFeedbackSending={isFeedbackSending}
        />
      }
      {
        isSubmissionAlertOpen && (
          <Box
            sx={{
              position: 'fixed',
              top: {xs: 8, sm: 16},
              left: smallScreen ? 0 : 312,
              right: 0,
              display: 'flex',
              justifyContent: 'center',
              zIndex: 1400,
              pointerEvents: 'none',
            }}
          >
            <Alert
              icon={<CheckCircleOutline fontSize="small"/>}
              severity="success"
              action={
                <IconButton
                  size="small"
                  onClick={() => setIsSubmissionAlertOpen(false)}
                  sx={{color: 'grey.600'}}
                >
                  <Close fontSize="small"/>
                </IconButton>
              }
              sx={{
                'alignItems': 'center',
                'bgcolor': '#FFFDFC',
                'color': 'text.primary',
                'boxShadow': '0px 8px 24px rgba(0, 0, 0, 0.12)',
                'borderRadius': 4,
                'minWidth': {xs: '90%', sm: 'auto'},
                'pointerEvents': 'auto',
                '& .MuiAlert-icon': {
                  color: isMatch ? '#68A873' : 'primary.main',
                },
              }}
            >
              <Typography variant="subtitle1" sx={{fontWeight: 500}}>
                <Translate
                  expression={`caregiverDetail.CaregiverFeedback.SubmissionAlert.${isMatch ? 'IsAMatch' : 'IsNotAMatch'}`}
                  substitute={{caregiverName: firstName}}
                />
              </Typography>
            </Alert>
          </Box>
        )
      }
    </>
  );
};

CaregiverFeedbackSection.propTypes = {
  clientRn: PropTypes.string.isRequired,
  caregiverRn: PropTypes.string.isRequired,
  questions: PropTypes.object.isRequired,
  createCaregiverFeedback: PropTypes.func.isRequired,
  getCaregiverFeedback: PropTypes.func.isRequired,
  statusProgressWrap: PropTypes.func.isRequired,
  userRn: PropTypes.string.isRequired,
};

const mapStateToProps = (state) => ({
  clientRn: state.user.clientRn,
  userRn: state.user.userRn,
});

export default connect(mapStateToProps, dispatch)(React.memo(CaregiverFeedbackSection));
